import React, { useRef, useEffect, useState } from "react";
import { Box, Button, Chip, Typography, Paper, IconButton } from "@mui/material";
import { VisibilityOutlined } from "@mui/icons-material";
import useBotLogs from "../../hooks/useBotLogs";

function BotLogs({ botId, onLogCountChange }) {
	const { logs, connected, clearLogs } = useBotLogs(botId);
	const logsEndRef = useRef(null);
	const [expandedLog, setExpandedLog] = useState(null);

	// Report log count changes
	useEffect(() => {
		if (onLogCountChange && Array.isArray(logs)) {
			onLogCountChange(logs.length);
		}
	}, [logs, onLogCountChange]);

	// Process logs to determine their type and format
	const formattedLogs = logs.map((log) => {
		// Extract main message and exception details
		let mainMessage = log.message;
		let exceptionDetails = "";
		let level = log.level || detectLogLevel(log.message);

		if (log.message.includes("Exception") || log.message.includes("error:")) {
			const parts = log.message.split("\n");
			if (parts.length > 1) {
				mainMessage = parts[0];
				exceptionDetails = parts.slice(1).join("\n");
			}
			if (!level || level === "info") level = "error";
		}

		return {
			...log,
			mainMessage,
			exceptionDetails,
			level,
			timestamp: log.timestamp || new Date().toISOString().replace("T", " ").substring(0, 19),
		};
	});

	// Auto-scroll to bottom when new logs arrive
	useEffect(() => {
		if (logsEndRef.current) {
			logsEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [logs]);

	// Helper function to detect log level from message content
	function detectLogLevel(message) {
		const lowerMessage = message.toLowerCase();
		if (
			lowerMessage.includes("error") ||
			lowerMessage.includes("exception") ||
			lowerMessage.includes("failed") ||
			lowerMessage.includes("sqlstate")
		) {
			return "error";
		} else if (lowerMessage.includes("warning") || lowerMessage.includes("warn") || lowerMessage.includes("stopped")) {
			return "warning";
		} else if (lowerMessage.includes("started") || lowerMessage.includes("connected") || lowerMessage.includes("success")) {
			return "success";
		} else {
			return "info";
		}
	}

	// Get chip color based on log level
	function getChipStyles(level) {
		switch (level) {
			case "error":
				return {
					bgcolor: "rgba(244, 67, 54, 0.1)",
					color: "rgb(244, 67, 54)",
					label: "ERROR",
				};
			case "warning":
				return {
					bgcolor: "rgba(255, 152, 0, 0.1)",
					color: "rgb(255, 152, 0)",
					label: "WARNING",
				};
			case "success":
				return {
					bgcolor: "rgba(76, 175, 80, 0.1)",
					color: "rgb(76, 175, 80)",
					label: "SUCCESS",
				};
			default:
				return {
					bgcolor: "rgba(33, 150, 243, 0.1)",
					color: "rgb(33, 150, 243)",
					label: "INFO",
				};
		}
	}

	const handleClearLogs = () => {
		clearLogs();
		if (onLogCountChange) {
			onLogCountChange(0);
		}
	};

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Box
				sx={{
					p: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					borderBottom: "1px solid",
					borderColor: "divider",
					bgcolor: "#f9f9f9",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Chip label={connected ? "Connected" : "Disconnected"} color={connected ? "success" : "error"} size="small" />
					<Button variant="outlined" size="small" onClick={handleClearLogs} sx={{ ml: 1 }}>
						Clear
					</Button>
				</Box>
			</Box>

			<Box
				sx={{
					flexGrow: 1,
					height: "calc(100vh - 180px)",
					overflowY: "auto",
					bgcolor: "#fff",
				}}
			>
				{formattedLogs.length === 0 ? (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
						<Typography color="text.secondary">No logs available. Start the bot to see logs.</Typography>
					</Box>
				) : (
					formattedLogs.map((log, index) => {
						const chipStyles = getChipStyles(log.level);

						return (
							<Paper
								key={index}
								elevation={0}
								sx={{
									p: 0,
									borderBottom: "1px solid #eaeaea",
									borderRadius: 0,
									"&:hover": {
										bgcolor: "rgba(0,0,0,0.01)",
									},
								}}
							>
								<Box
									sx={{
										px: 3,
										py: 2,
										display: "flex",
										justifyContent: "space-between",
										alignItems: "flex-start",
									}}
								>
									<Box sx={{ width: "65%" }}>
										<Typography
											variant="body1"
											sx={{
												mb: 0.5,
												fontWeight: 400,
												color: "#333",
												fontFamily: log.level === "error" ? "monospace" : "inherit",
												fontSize: log.level === "error" ? "0.9rem" : "inherit",
											}}
										>
											{log.mainMessage}
										</Typography>

										{log.exceptionDetails && (
											<Typography
												variant="body2"
												sx={{
													color: "text.secondary",
													fontFamily: "monospace",
													fontSize: "0.85rem",
												}}
											>
												{log.exceptionDetails}
											</Typography>
										)}
									</Box>

									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										<Chip
											label={chipStyles.label}
											sx={{
												bgcolor: chipStyles.bgcolor,
												color: chipStyles.color,
												fontWeight: 500,
												fontSize: "0.75rem",
												height: 24,
												minWidth: 65,
											}}
										/>

										<Typography
											variant="body2"
											sx={{
												color: "text.secondary",
												width: 180,
												textAlign: "right",
											}}
										>
											{log.timestamp}
										</Typography>

										<IconButton size="small" sx={{ color: "rgba(0,0,0,0.4)" }} onClick={() => setExpandedLog(expandedLog === index ? null : index)}>
											<VisibilityOutlined fontSize="small" />
										</IconButton>
									</Box>
								</Box>

								{expandedLog === index && (
									<Box
										sx={{
											px: 3,
											py: 2,
											bgcolor: "rgba(0,0,0,0.03)",
											borderTop: "1px solid #eaeaea",
										}}
									>
										<Typography
											variant="body2"
											sx={{
												fontFamily: "monospace",
												whiteSpace: "pre-wrap",
											}}
										>
											{JSON.stringify(
												{
													message: log.message,
													timestamp: log.timestamp,
													level: log.level,
													// Add more details here as needed
												},
												null,
												2
											)}
										</Typography>
									</Box>
								)}
							</Paper>
						);
					})
				)}
				<div ref={logsEndRef} />
			</Box>
		</Box>
	);
}

// Default props
BotLogs.defaultProps = {
	onLogCountChange: () => {},
};

export default BotLogs;
